import React, { useEffect, useMemo, useState } from "react";
import { Button, Table, Tag, Tooltip } from "antd";
import "./threeM.css";
import { CheckOutlined, WarningOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import {
  FsFn,
  PhasingIndex,
  ThreeMPhaseNode,
  ThreeMRollup,
  TreeRow,
  allExpandableKeys,
  buildTree,
  fmtINR,
  fmtQty,
  getStatusColor,
  num,
  schedPct,
  threeMRowClassName,
} from "./threeMShared";
import { fmtMonth } from "../MonthlyPnl/pnlShared";
import { useFontScale } from "../../../../theme/fontScale";
import { useWbsTreeName } from "../../shared/useWbsCodes";
import { boqBasisLine, perUnitLine } from "./threeMCells";

const amountCell = (value: number) =>
  value > 0 ? (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(value)}</span>
  ) : (
    <span style={{ color: "#bfbfbf", fontVariantNumeric: "tabular-nums" }}>
      ₹0.00
    </span>
  );

// Allotted material quantity behind the row's material cost. The rollup also
// carries requested / ordered / issued / utilized, but those belong to the
// procurement flow and read as a row of zeros here, so the tooltip states the
// allotted figure only.
const QtyFlowTooltip: React.FC<{
  rollup: ThreeMRollup;
  children: React.ReactNode;
}> = ({ rollup, children }) => {
  const { fs } = useFontScale();
  return (
  <Tooltip
    title={
      <div style={{ fontSize: fs(12), lineHeight: 1.7 }}>
        Allotted: {fmtQty(rollup.allottedQuantity)}
      </div>
    }
    placement="left"
  >
    <span style={{ cursor: "help", borderBottom: "1px dotted #bfbfbf" }}>
      {children}
    </span>
  </Tooltip>
  );
};

const legendItem = (color: string, label: string, fs: FsFn) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: fs(12),
      color: "#595959",
    }}
  >
    <span
      style={{
        display: "inline-block",
        width: 14,
        height: 4,
        background: color,
        borderRadius: 2,
      }}
    />
    {label}
  </span>
);

const ConsolidationTree: React.FC<{
  phases: ThreeMPhaseNode[];
  // Schedule index, so each node can report when it runs and how much of its
  // cost is actually phased into months.
  index: PhasingIndex;
  onViewTask: (taskId: number, taskName: string) => void;
}> = ({ phases, index, onViewTask }) => {
  // scale drives column widths too: at A+/A++ the full-precision fmtINR
  // amounts overflow fixed-width columns unless the grid grows with the text
  // (same treatment as PlannedVsActualTree).
  const { fs, scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);
  // scroll.x MUST be >= the sum of the rendered column widths or the fixed
  // name column misaligns; sum the SAME rounded values used by the columns
  // (rounding w(total) separately can come out a few px short at A+/A++).
  const scrollX =
    w(340) + w(90) + w(140) + w(130) + w(130) + w(150) +
    w(90) + w(90) + w(110) + w(100);
  const treeData = useMemo(() => buildTree(phases), [phases]);
  // Phase/activity names embed a WBS code; task names are already clean.
  const treeLabel = useWbsTreeName(treeData);
  const expandKeys = useMemo(() => allExpandableKeys(treeData), [treeData]);

  // Controlled expansion so "expand all phases" survives async data loads.
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>(
    []
  );
  useEffect(() => {
    // Collapsed by default: the WBS reads as a phase-level summary until
    // you drill in, and expanding 963 tasks up front is unusable.
    setExpandedRowKeys([]);
  }, [treeData]);

  const columns: ColumnsType<TreeRow> = [
    {
      title: "Phase / Activity / Task",
      dataIndex: "name",
      key: "name",
      width: w(340),
      fixed: "left",
      render: (text: string, record: TreeRow) => {
        const key = record.key || "";
        const statusTag = record.status ? (
          <Tag
            color={getStatusColor(record.status)}
            style={{ fontSize: fs(10.5), lineHeight: 1.5, marginLeft: 6 }}
          >
            {record.status}
          </Tag>
        ) : null;

        // Block-level flex column: sits BESIDE the floated indent/expand icon
        // (threeM.css) taking the remaining width, so wrapped lines stay
        // aligned under the cell's own left edge instead of dropping below
        // the tree indent. Must stay block-level (not inline-flex) — atomic
        // inline boxes drop to the next line when the icon leaves too little
        // room, and `td { display: flex }` (the old approach) broke the
        // frozen column's sticky painting.
        const cellBox: React.CSSProperties = {
          display: "flex",
          flexDirection: "column",
          gap: 2,
          textAlign: "left",
          minWidth: 0,
        };

        // Aggregated Σ work qty @ BOQ rate = BOQ value. Quantity is present
        // only when the child-task UOMs are uniform; the value shows either
        // way (rupees sum across UOMs). The UOM lives in its own column, so
        // the line does not repeat it.
        const aggQtyLine = boqBasisLine(record, fs, {
          prefix: "Σ Work qty: ",
          showUom: false,
        });

        if (key.startsWith("P-")) {
          return (
            <div style={cellBox}>
              <span
                style={{ fontWeight: 700, fontSize: fs(13.5), color: "#1f1f1f" }}
              >
                {treeLabel(key, text)}
                {statusTag}
              </span>
              {aggQtyLine}
            </div>
          );
        }
        if (key.startsWith("A-")) {
          return (
            <div style={cellBox}>
              <span
                style={{ fontWeight: 600, fontSize: fs(12.5), color: "#262626" }}
              >
                {treeLabel(key, text)}
                {statusTag}
              </span>
              {aggQtyLine}
            </div>
          );
        }
        return (
          <div style={cellBox}>
            {record.sequenceId ? (
              <span style={{ fontSize: fs(11), color: "#8c8c8c" }}>
                {record.sequenceId}
              </span>
            ) : null}
            <span style={{ fontSize: fs(12.5), color: "#262626" }}>
              <span
                className="inlineTableCellLink"
                onClick={() => onViewTask(record.id, record.name)}
              >
                {text}
              </span>
              {statusTag}
            </span>
            {boqBasisLine(record, fs, {
              prefix: "Work qty: ",
              showUom: false,
            })}
          </div>
        );
      },
    },
    {
      title: "UOM",
      key: "uom",
      width: w(90),
      align: "center",
      render: (_: any, record: TreeRow) =>
        // Phase/activity rows carry a UOM only when every child task shares
        // one (aggWorkBasis) — mixed-UOM parents show a muted dash.
        record.workUom ? (
          <span style={{ fontSize: fs(12) }}>{record.workUom}</span>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
    {
      title: "Material ₹",
      key: "materialAmount",
      width: w(140),
      align: "right",
      render: (_: any, record: TreeRow) => {
        const cell = amountCell(record.rollup.materialAmount);
        if (record.key.startsWith("T-")) {
          return (
            <div>
              <QtyFlowTooltip rollup={record.rollup}>{cell}</QtyFlowTooltip>
              {perUnitLine(record, record.rollup.materialAmount, fs)}
            </div>
          );
        }
        return cell;
      },
    },
    {
      title: "Manpower ₹",
      key: "manpowerAmount",
      width: w(130),
      align: "right",
      render: (_: any, record: TreeRow) => (
        <div>
          {amountCell(record.rollup.manpowerAmount)}
          {perUnitLine(record, record.rollup.manpowerAmount, fs)}
        </div>
      ),
    },
    {
      title: "Machinery ₹",
      key: "machineryAmount",
      width: w(130),
      align: "right",
      render: (_: any, record: TreeRow) => (
        <div>
          {amountCell(record.rollup.machineryAmount)}
          {perUnitLine(record, record.rollup.machineryAmount, fs)}
        </div>
      ),
    },
    {
      title: "Total ₹",
      key: "totalAmount",
      width: w(150),
      align: "right",
      render: (_: any, record: TreeRow) => (
        <div>
          <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {fmtINR(record.rollup.totalAmount)}
          </span>
          {perUnitLine(record, record.rollup.totalAmount, fs)}
        </div>
      ),
    },
    {
      title: "Start",
      key: "schedStart",
      width: w(90),
      align: "center",
      render: (_: any, record: TreeRow) => {
        const start = index.byKey.get(record.key)?.start;
        return start ? (
          <span style={{ fontSize: fs(12) }}>{fmtMonth(start)}</span>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        );
      },
    },
    {
      title: "Finish",
      key: "schedFinish",
      width: w(90),
      align: "center",
      render: (_: any, record: TreeRow) => {
        const finish = index.byKey.get(record.key)?.finish;
        return finish ? (
          <span style={{ fontSize: fs(12) }}>{fmtMonth(finish)}</span>
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        );
      },
    },
    {
      // How much of this node's cost is phased into any month. Anything not
      // phased is invisible in every month-wise forecast downstream, so 0%
      // with real cost is the one state that earns a warning.
      title: "Sched %",
      key: "schedPct",
      width: w(110),
      align: "right",
      render: (_: any, record: TreeRow) => {
        const node = index.byKey.get(record.key);
        const scheduled = node?.scheduled.total ?? 0;
        const planned = num(record.rollup.totalAmount);
        const pct = schedPct(scheduled, planned);
        // Nothing to phase is not a problem — no number, no warning.
        if (pct === null) return <span style={{ color: "#bfbfbf" }}>—</span>;

        const over = pct > 100.05;
        const full = pct >= 99.95 && !over;
        const none = scheduled === 0;
        const color = none || over ? "#cf1322" : full ? "#237804" : "#d46b08";
        const tip = (
          <div style={{ fontVariantNumeric: "tabular-nums" }}>
            <div>
              {fmtINR(scheduled)} of {fmtINR(planned)} phased
            </div>
            <div>{fmtINR(planned - scheduled)} unphased</div>
            {over ? (
              <div style={{ marginTop: 4 }}>
                Scheduled beyond the work quantity on one or more tasks.
              </div>
            ) : none ? (
              <div style={{ marginTop: 4 }}>
                No monthly schedule, or no work quantity to phase against — this
                cost is invisible in every month-wise forecast.
              </div>
            ) : null}
          </div>
        );
        return (
          <Tooltip title={tip}>
            <span
              style={{
                color,
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
              }}
            >
              {pct.toFixed(pct >= 99.95 && !over ? 0 : 1)}%
              {none ? (
                <WarningOutlined style={{ marginLeft: 4, fontSize: fs(11) }} />
              ) : null}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Norm",
      key: "norm",
      width: w(100),
      align: "center",
      render: (_: any, record: TreeRow) => {
        if (record.rollup.normLineCount > 0) {
          return (
            <Tag color="purple" style={{ marginRight: 0 }}>
              {record.rollup.normLineCount} line
              {record.rollup.normLineCount === 1 ? "" : "s"}
            </Tag>
          );
        }
        if (record.rollup.hasNorm) {
          return <CheckOutlined style={{ color: "#722ed1" }} />;
        }
        return <span style={{ color: "#bfbfbf" }}>—</span>;
      },
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 18,
          padding: "6px 12px",
          marginBottom: 8,
          background: "#fafafa",
          border: "1px solid #f0f0f0",
          borderRadius: 4,
        }}
      >
        {legendItem("#722ed1", "Phase", fs)}
        {legendItem("#fa8c16", "Activity", fs)}
        {legendItem("#1677ff", "Task", fs)}
        <div style={{ marginLeft: "auto" }}>
          <Button
            type="link"
            size="small"
            disabled={expandKeys.length === 0}
            onClick={() => setExpandedRowKeys(expandKeys)}
          >
            Expand all
          </Button>
          <Button
            type="link"
            size="small"
            disabled={expandedRowKeys.length === 0}
            onClick={() => setExpandedRowKeys([])}
          >
            Collapse all
          </Button>
        </div>
      </div>
      <Table
        className="customTable taskHierarchyTable threeMTree"
        columns={columns}
        dataSource={treeData}
        pagination={false}
        bordered
        size="middle"
        // Header stays pinned to the viewport top while scrolling the tree
        // (matches the Planned vs Actual tab; app nav is not position-fixed).
        sticky={{ offsetHeader: 0 }}
        scroll={{ x: scrollX }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
        }}
        rowClassName={threeMRowClassName}
      />
    </>
  );
};

export default ConsolidationTree;
